/** Display formatting helpers for the semantic summary engine. */

export function formatMoney(n: number, currency = "USD"): string {
  const symbol = currency === "USD" || currency === "" ? "$" : `${currency} `;
  if (n >= 1_000_000_000) return `${symbol}${trimZeros((n / 1_000_000_000).toFixed(1))}B`;
  if (n >= 1_000_000) return `${symbol}${trimZeros((n / 1_000_000).toFixed(1))}M`;
  if (n >= 1_000) return `${symbol}${trimZeros((n / 1_000).toFixed(0))}K`;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function trimZeros(s: string): string {
  return s.replace(/\.0$/, "");
}

export function formatCompactNumber(n: number): string {
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n);
}

/** Safe display string for unknown values (objects become JSON, not "[object Object]"). */
export function toDisplayString(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean" || typeof v === "bigint") return String(v);
  try {
    return JSON.stringify(v) ?? "";
  } catch {
    return "";
  }
}

/** Strip markdown escapes leaked from the backend (e.g. SERIES\_A). */
export function sanitizeText(s: string): string {
  return s.replace(/\\_/g, "_");
}

/** Trailing tokens that end with a period but do not end a sentence. */
const NON_TERMINAL_ABBREVIATIONS = new Set([
  "e.g",
  "i.e",
  "etc",
  "vs",
  "approx",
  "est",
  "inc",
  "ltd",
  "co",
  "corp",
  "dr",
  "mr",
  "mrs",
  "ms",
  "no",
  "st",
  "u.s",
  "u.k",
]);

/**
 * Split prose into sentences, guarding against abbreviations, initials, and
 * decimals so financial copy ("$1.8 million", "e.g. SaaS") stays intact.
 */
export function splitSentences(text: string): string[] {
  const sentences: string[] = [];
  let current = "";

  for (let i = 0; i < text.length; i++) {
    current += text[i];
    const ch = text[i];
    if (ch !== "." && ch !== "!" && ch !== "?") continue;

    // A boundary needs whitespace followed by a plausible sentence opener.
    const rest = text.slice(i + 1);
    if (!/^\s+["'(]?[A-Z0-9$€£]/.test(rest)) continue;

    if (ch === ".") {
      const beforePeriod = current.slice(0, -1);
      const lastToken = (/[A-Za-z][A-Za-z.]*$/.exec(beforePeriod)?.[0] ?? "").toLowerCase();
      // "e.g." / "vs." / "Dr." — not sentence ends; nor single-letter initials.
      if (NON_TERMINAL_ABBREVIATIONS.has(lastToken) || /^[a-z]$/.test(lastToken)) continue;
    }

    sentences.push(current.trim());
    current = "";
    while (i + 1 < text.length && /\s/.test(text[i + 1])) i++;
  }

  if (current.trim()) sentences.push(current.trim());
  return sentences;
}

const PARAGRAPH_MAX_SENTENCES = 3;
const PARAGRAPH_MAX_CHARS = 500;

/**
 * Break a wall-of-text summary into scannable paragraphs — losslessly.
 * Sentences are preserved verbatim and in order; only the whitespace between
 * them changes. Text that already has paragraph breaks is returned as-is.
 */
export function formatProseParagraphs(text: string): string {
  const trimmed = text.trim();
  if (!trimmed || trimmed.includes("\n\n")) return trimmed;

  const sentences = splitSentences(trimmed);
  if (sentences.length <= 2) return trimmed;

  const paragraphs: string[] = [];
  let current: string[] = [];
  let currentLength = 0;

  for (const sentence of sentences) {
    const wouldOverflow =
      current.length >= PARAGRAPH_MAX_SENTENCES ||
      (current.length > 0 && currentLength + sentence.length > PARAGRAPH_MAX_CHARS);
    if (wouldOverflow) {
      paragraphs.push(current.join(" "));
      current = [];
      currentLength = 0;
    }
    current.push(sentence);
    currentLength += sentence.length + 1;
  }
  if (current.length > 0) paragraphs.push(current.join(" "));

  return paragraphs.join("\n\n");
}

/** "SERIES_A" | "early-stage venture capital firm" → "Series A" / title-cased words. */
export function humanizeEnum(s: string): string {
  return sanitizeText(s)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\bAi\b/g, "AI")
    .replace(/\bSaas\b/g, "SaaS");
}
